import { useEffect, useState, useMemo, useRef, useCallback } from "react";
import { Marker, Popup } from "react-leaflet";
import { useSelector, shallowEqual } from "react-redux";
import * as L from 'leaflet';
import lightningIcon1 from "../../assets/lightning_m_1.svg";
import lightningIcon2 from "../../assets/lightning_m_2.svg";
import lightningIcon3 from "../../assets/lightning_m_3.svg";
import { StrikeData, MessageData, LightningData, CompressedLightningData } from "../../interfaces/lightning";
import { RootState } from "../../store";
import { TWS_WS_URL } from "../../environment/apis.config";
import { debounce} from "lodash";
import "./Lightning.css";
import { MemoLightningMarker } from "./MemoLightMarker";
import ReconnectingWebSocket from "reconnecting-websocket";
import useErrorStatus from "../../hooks/UseErrorStatus";
import React from "react";

// Icon instances
const lightningIconInstance1 = new L.Icon({
  iconUrl: lightningIcon1,
  iconSize: new L.Point(20, 20),
  iconAnchor: new L.Point(10, 10)
});
const lightningIconInstance2 = new L.Icon({
  iconUrl: lightningIcon2,
  iconSize: new L.Point(20, 20),
  iconAnchor: new L.Point(10, 10)
});
const lightningIconInstance3 = new L.Icon({
    iconUrl: lightningIcon3,
    iconSize: new L.Point(20, 20),
    iconAnchor: new L.Point(10, 10),
});

const pulseBG_light = new L.divIcon({
    className: 'pulse-icon', // Optional for additional styles
    html: '<div class="pulsating-marker-light"></div>', // The div with the pulse animation
    iconSize: [30, 30], // Size of the marker icon
    iconAnchor: [25, 25]
});

const pulseBG_dark = new L.divIcon({
  className: 'pulse-icon', // Optional for additional styles
  html: '<div class="pulsating-marker-dark"></div>', // The div with the pulse animation
  iconSize: [30, 30], // Size of the marker icon
  iconAnchor: [25, 25]
});


const UpdatedLightning = () => {
    const token = localStorage.getItem("token");
    const url = `${TWS_WS_URL}?token=${token}`;
    const wsRef = useRef<ReconnectingWebSocket | null>(null);
    const [lightningData, setLightningData] = useState<CompressedLightningData[]>([]);
    const messageChunks = useRef<CompressedLightningData[]>([])
    const darkMode = useSelector((state: RootState) => state.mapState.darkMode)
    const notificationStatus = useErrorStatus()

    const location = useSelector(
        (state: RootState) => state.user.defaultLocation,
        shallowEqual
    );
    const radius = 100;

    const connectMessage = {
        message_type: "connect",
        dataFeed: "lightning",
        filterType: "point",
        lat: Number(location.latitude),
        long: Number(location.longitude),
        radius,
    };

    const disconnectMessage = {
        message_type: "disconnect",
        dataFeed: "lightning",
        filterType: "point",
        lat: Number(location.latitude),
        long: Number(location.longitude),
        radius,
    };

    // WebSocket connection setup
    useEffect(() => {
        const ws = new ReconnectingWebSocket(url, [], {
            maxRetries: 10,
            maxReconnectionDelay: 5000 //time in ms
        });
        wsRef.current = ws;
        notificationStatus.addMessage('Lightning service enabled for current location.', 200, 'info', false, 10 * 1000)

        ws.onopen = () =>{ 
            console.log('WebSocket connection established');
            setLightningData([])
        }
        ws.onclose = () => console.log('WebSocket connection was closed');

        ws.onerror = (error) => {
            console.error('WebSocket error:', error);
            wsRef.current = null;
        };

        ws.onmessage = (event) => {
            const parsedData = JSON.parse(event.data);
            //Check if connections is online
            //Only send message if we have online connection 
            if(parsedData.msg_type === "info" && parsedData.connection_status === "online") ws.send(JSON.stringify(connectMessage));

            //Parse lightning data
            if (parsedData.msg_type === "data") {                
                var processedData:CompressedLightningData = {
                    id: parsedData.id,
                    lat: parsedData.loc.lat,
                    long: parsedData.loc.long,
                    dateTimeISO: parsedData.ob.dateTimeISO,
                    pulseMarker: true
                }
                messageChunks.current.push(processedData);
                updateLightningData();
            }
        };
        
        //Cleanup
        return () => {
            if (wsRef.current?.readyState === WebSocket.OPEN) {
                wsRef.current.send(JSON.stringify(disconnectMessage));
                wsRef.current = null;
                ws.close()
            }
            updateLightningData.cancel()
        };
    },[location]);

    //Debounced function used to update the UI data
    const updateLightningData = useCallback(
        //Need to use prevData as directly using lightingData gives us stale value
        debounce(() => {
            setLightningData(prevData => {
                //console.log("Prev :", prevData.length);
                for(let prevPackets of prevData) prevPackets['pulseMarker'] = false;
                const combinedData = [...prevData, ...messageChunks.current]
                //console.log("Combined :", combinedData.length);
                
                const uniqueData = Array.from(
                    new Map(combinedData.map(item => [item.id, item])).values()
                );
                //console.log("Unique :", uniqueData.length);
                const filteredData = uniqueData.filter((item) => {
                    const currentTime = new Date();
                    const strikeTime = new Date(item.dateTimeISO)
                    const msInOneMinute = 1000 * 60;
                    const differenceInMs = currentTime.getTime() - strikeTime.getTime();
                    const differenceInMinutes = Math.floor(differenceInMs / msInOneMinute);
                    if(differenceInMinutes > 10) return false
                    else return true
                });
                //console.log("Filtered :", filteredData.length);
                messageChunks.current = [];
                return filteredData
            })            
        }, 3000),
        []
    )

    // Addresses the case where we dont receive any new data
    useEffect(() => {
        const intervalId = setInterval(() => {
            updateLightningData()
        }, 5000);

        return () => { 
            clearInterval(intervalId); // Clean up on unmount
            updateLightningData.cancel()
        }
    }, []);

    //Utility functions
    function getIcon(dateTimeISO: string){
        const currentTime = new Date();
        const strikeTime = new Date(dateTimeISO);
        const differenceInMs = currentTime.getTime() - strikeTime.getTime();
        const msInOneMinute = 1000 * 60;
        const differenceInMinutes = Math.floor(differenceInMs / msInOneMinute);
        
        if(differenceInMinutes <= 2) return lightningIconInstance1;
        else if(differenceInMinutes > 2 && differenceInMinutes <= 5) return lightningIconInstance2;
        else return lightningIconInstance3;
    }
    function getZindex(dateTimeISO: string){
        const currentTime = new Date();
        const strikeTime = new Date(dateTimeISO);
        const differenceInMs = currentTime.getTime() - strikeTime.getTime();
        const msInOneMinute = 1000 * 60;
        const differenceInMinutes = Math.floor(differenceInMs / msInOneMinute);
        
        if(differenceInMinutes <= 2) return 30;
        else if(differenceInMinutes > 2 && differenceInMinutes <= 5) return 20;
        else return 10;
    }
    return (
        <>
            {
                lightningData.map((data) => (
                    <React.Fragment key={data.id}>
                        { 
                            data.pulseMarker &&
                            <Marker
                            key={`pulse-${data.id}`}
                            position={[parseFloat(data.lat), parseFloat(data.long)]}
                            icon={darkMode ? pulseBG_light : pulseBG_dark}
                            />
                        }
                        <MemoLightningMarker
                            key={data.id}
                            id={data.id}
                            position={[parseFloat(data.lat), parseFloat(data.long)]}
                            icon={getIcon(data.dateTimeISO)}
                            zIndex={getZindex(data.dateTimeISO)}
                        />
                    </React.Fragment>
                ))
            }
        </>
    );
};

export default UpdatedLightning;
